import { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import Specimen from '../components/library/Specimens'
import { components } from '../content/components'
import { displayFonts, monoFont, sansFonts } from '../content/fonts'
import { themeFor } from '../content/missions'
import { deriveTheme, roleDescriptions, roleNames } from '../content/theme'
import { SYSTEM_VERSION, tokens } from '../content/system'
import { usePrefs } from '@/state/prefs'
import { useBrand } from '@/state/useBrand'
import { useCustomRoles } from '@/state/useCustomRoles'
import { OPERATOR } from '@/content/provenance'

const typeSamples = [
  ['h1', 'Heading 1'],
  ['h2', 'Heading 2'],
  ['h3', 'Heading 3'],
  ['lead', 'Lead paragraph'],
  ['body', 'Body text'],
  ['small', 'Small text'],
] as const

/**
 * The current design as one printable page: colors, type, space, shape and
 * every component. Print it, or save it as a PDF from the browser. It stands
 * alone, without the site's frame, so the print is just the specification.
 */
export default function SpecSheetPage() {
  const { prefs } = usePrefs()
  const brand = useBrand()
  const customRoles = useCustomRoles()
  const theme = useMemo(() => {
    if (!prefs.palette) return themeFor(brand.mission)
    const [primary, secondary, accent] = prefs.palette.split(',')
    return deriveTheme({ primary, secondary, accent })
  }, [prefs.palette, brand.mission])
  const display = displayFonts.find((font) => font.id === prefs.fontDisplay) ?? displayFonts[0]
  const sans = sansFonts.find((font) => font.id === prefs.fontSans) ?? sansFonts[0]

  useEffect(() => {
    document.title = `${brand.name} specification`
  }, [brand.name])

  return (
    <div className="spec" data-spec-root="">
      <main className="mds-container spec__main">
        <header className="spec__head">
          <p className="mds-eyebrow">
            Mission Design System {SYSTEM_VERSION} · {brand.mission.name}
          </p>
          <h1 className="mds-display">{brand.name}</h1>
          <p className="mds-lead">
            The specification of this design: color roles in light and dark, typefaces and the type scale, space, shape and
            every component.
          </p>
          <p className="mds-cluster spec__actions">
            <button type="button" className="mds-button mds-button--primary mds-button--sm" onClick={() => window.print()}>
              Print or save as PDF
            </button>
            <Link className="mds-link" to="/design-tools">
              Back to design tools
            </Link>
          </p>
        </header>

        <section className="spec__section" aria-labelledby="spec-color">
          <h2 id="spec-color" className="mds-h2">
            Color
          </h2>
          <div className="spec__modes">
            {(['light', 'dark'] as const).map((mode) => (
              <div key={mode} className="spec__mode" data-theme={mode} data-mission={prefs.mission} style={customRoles(mode)}>
                <h3 className="mds-h3">{mode === 'light' ? 'Light' : 'Dark'}</h3>
                <ul className="spec__swatches">
                  {roleNames.map((name) => (
                    <li key={name}>
                      <span className="spec__chip" style={{ background: theme[mode][name] }} aria-hidden="true" />
                      <span>
                        <strong>{name}</strong>
                        <code>{theme[mode][name]}</code>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <dl className="spec__roles">
            {roleNames.map((name) => (
              <div key={name}>
                <dt>
                  <code>--mds-{name}</code>
                </dt>
                <dd>{roleDescriptions[name]}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="spec__section" aria-labelledby="spec-type">
          <h2 id="spec-type" className="mds-h2">
            Typography
          </h2>
          <ul className="spec__fonts">
            {[
              ['Headlines', display],
              ['Body', sans],
              ['Code', monoFont],
            ].map(([role, font]) => (
              <li key={role as string}>
                <span className="mds-eyebrow">{role as string}</span>
                <span className="spec__font" style={{ fontFamily: (font as typeof display).stack }}>
                  {(font as typeof display).name}
                </span>
                <span className="mds-hint">{(font as typeof display).package}</span>
              </li>
            ))}
          </ul>
          <ul className="spec__scale">
            {typeSamples.map(([key, label]) => (
              <li key={key}>
                <code>text.{key}</code>
                <span className={key.startsWith('h') ? `mds-${key}` : key === 'lead' ? 'mds-lead' : undefined} style={key === 'small' ? { fontSize: '0.875rem' } : undefined}>
                  {label}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <section className="spec__section" aria-labelledby="spec-space">
          <h2 id="spec-space" className="mds-h2">
            Layout and shape
          </h2>
          <ul className="spec__space">
            {tokens
              .filter((token) => token.group === 'spacing')
              .map((token) => (
                <li key={token.name}>
                  <code>{token.name}</code>
                  <span className="spec__bar" style={{ width: token.value }} aria-hidden="true" />
                  <span className="mds-hint">{token.value}</span>
                </li>
              ))}
          </ul>
          <ul className="spec__radii">
            {tokens
              .filter((token) => token.group === 'shape')
              .map((token) => (
                <li key={token.name}>
                  <span className="spec__radius" style={{ borderRadius: token.value }} aria-hidden="true" />
                  <code>{token.name}</code>
                </li>
              ))}
          </ul>
        </section>

        <section className="spec__section" aria-labelledby="spec-components">
          <h2 id="spec-components" className="mds-h2">
            Components
          </h2>
          <ul className="spec__components">
            {components.map((component) => (
              <li key={component.id}>
                <div className="spec__specimen" inert aria-hidden="true">
                  <Specimen id={component.id} mini />
                </div>
                <span>{component.name}</span>
              </li>
            ))}
          </ul>
        </section>
      </main>
      <footer className="mds-container spec__footer">
        <p className="mds-hint">
          Mission Design System {SYSTEM_VERSION}. Built by <a href={OPERATOR.url}>{OPERATOR.name}</a>.
        </p>
      </footer>
    </div>
  )
}
